<script setup lang="ts">
import { computed } from 'vue';
import type { FetchRecipeResponseLite } from 'src/api/models/fetchRecipeResponseLite';
import SharedAutocomplete from 'src/components/shared/SharedAutocomplete.vue';
import { formatCurrency } from 'src/utils/currency';
import {
  emptyRecipeEntry,
  isSupportedRecipeType,
  type RecipeEntry,
  type RecipePricingMode,
} from 'src/composables/useContractManagement';

const props = defineProps<{
  readonly?: boolean;
  lockRecipes?: boolean;
  submitted?: boolean;
  searchRecipesFn: (query: string, page: number) => Promise<{ data: FetchRecipeResponseLite[]; page: number; total_pages: number }>;
}>();

const model = defineModel<RecipeEntry[]>({ required: true });

const PRICING_MODE_OPTIONS: { label: string; value: RecipePricingMode }[] = [
  { label: 'Vaste prijs', value: 'fixed' },
  { label: 'Uurtarief', value: 'hourly' },
];

const selectedRecipes = computed<(FetchRecipeResponseLite | null)[]>(() =>
  (model.value ?? []).map((entry) => {
    if (!entry?.recipe_id) return null;
    return {
      id: entry.recipe_id,
      name: entry.recipe_name ?? '',
      type: entry.recipe_type ?? undefined,
    } as FetchRecipeResponseLite;
  }),
);

function onRecipeSelect(index: number, recipe: FetchRecipeResponseLite) {
  const next = [...model.value];
  const previous = next[index] ?? emptyRecipeEntry();
  next[index] = {
    ...previous,
    recipe_id: recipe.id ?? null,
    recipe_name: recipe.name ?? null,
    recipe_type: recipe.type ?? null,
    pricing_mode: previous.pricing_mode ?? 'fixed',
  };
  model.value = next;
}

function onRecipeClear(index: number) {
  const next = [...model.value];
  const previous = next[index] ?? emptyRecipeEntry();
  next[index] = { ...emptyRecipeEntry(), action_item_id: previous.action_item_id };
  model.value = next;
}

type RecipeNumericField = 'tariff' | 'interval' | 'hourly_running_rate' | 'running_hours_per_year';

function updateField(index: number, field: RecipeNumericField, value: number | null) {
  const next = [...model.value];
  next[index] = { ...next[index], [field]: value };
  model.value = next;
}

function updatePricingMode(index: number, mode: RecipePricingMode | null) {
  if (!mode) return;
  const next = [...model.value];
  next[index] = { ...next[index], pricing_mode: mode };
  model.value = next;
}

function addRecipe() {
  model.value = [...model.value, emptyRecipeEntry()];
}

function removeRecipe(index: number) {
  const next = [...model.value];
  next.splice(index, 1);
  model.value = next;
}

function hasRecipe(entry: RecipeEntry | undefined): boolean {
  return !!entry?.recipe_id;
}

function recipeType(entry: RecipeEntry | undefined): string | null {
  return entry?.recipe_type ?? null;
}

function isService(entry: RecipeEntry | undefined): boolean {
  return entry?.recipe_type === 'service';
}

function isInspection(entry: RecipeEntry | undefined): boolean {
  return entry?.recipe_type === 'inspection';
}

function isHourly(entry: RecipeEntry | undefined): boolean {
  return isService(entry) && entry?.pricing_mode === 'hourly';
}

function isUnsupported(entry: RecipeEntry | undefined): boolean {
  return hasRecipe(entry) && !isSupportedRecipeType(entry?.recipe_type);
}

function showFixedPriceFields(entry: RecipeEntry | undefined): boolean {
  if (isInspection(entry)) return true;
  if (isService(entry) && entry?.pricing_mode === 'fixed') return true;
  return false;
}

function tariffError(entry: RecipeEntry | undefined): string {
  if (!props.submitted) return '';
  return showFixedPriceFields(entry) && (entry?.tariff === null || entry?.tariff === undefined)
    ? 'Verplicht wanneer een recipe is geselecteerd'
    : '';
}

function intervalError(entry: RecipeEntry | undefined): string {
  if (!props.submitted) return '';
  if (!hasRecipe(entry)) return '';
  if (!isSupportedRecipeType(entry?.recipe_type)) return '';
  return entry?.interval === null || entry?.interval === undefined
    ? 'Verplicht wanneer een recipe is geselecteerd'
    : '';
}

function hourlyRateError(entry: RecipeEntry | undefined): string {
  if (!props.submitted) return '';
  return isHourly(entry) && (entry?.hourly_running_rate === null || entry?.hourly_running_rate === undefined)
    ? 'Verplicht voor uurtarief'
    : '';
}

function runningHoursPerYearError(entry: RecipeEntry | undefined): string {
  if (!props.submitted) return '';
  return isHourly(entry) && (entry?.running_hours_per_year === null || entry?.running_hours_per_year === undefined)
    ? 'Verplicht voor uurtarief'
    : '';
}

function formatTariff(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return formatCurrency(value);
}

const canRemove = computed(() => model.value.length > 1);
</script>

<template>
  <div class="flex flex-col gap-4">
    <h4 class="text-sm font-semibold text-color-secondary uppercase tracking-wide">Recipes</h4>

    <div
      v-for="(entry, index) in model"
      :key="index"
      class="flex flex-col gap-2 rounded-md border border-surface-200 dark:border-surface-700 p-3"
      :data-testid="`contract-recipe-row-${index}`"
    >
      <div class="flex items-center justify-between">
        <Tag
          v-if="recipeType(entry)"
          :data-testid="`contract-recipe-type-${index}`"
          :severity="isUnsupported(entry) ? 'warn' : 'info'"
          :value="recipeType(entry)"
        />
        <span v-else class="text-xs text-color-secondary italic">Geen recipe geselecteerd</span>
        <Button
          v-if="canRemove && !readonly && !lockRecipes"
          :data-testid="`contract-recipe-remove-${index}`"
          icon="pi pi-trash"
          severity="danger"
          text
          rounded
          size="small"
          aria-label="Verwijder recipe"
          @click="removeRecipe(index)"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Recipe</label>
        <div
          v-if="lockRecipes"
          :data-testid="`contract-recipe-locked-${index}`"
          class="text-sm font-medium px-2 py-1.5 rounded bg-surface-100 dark:bg-surface-800"
        >
          {{ entry.recipe_name ?? '-' }}
        </div>
        <SharedAutocomplete
          v-else
          :data-testid="`contract-recipe-${index}`"
          :model-value="selectedRecipes[index]"
          :fetch-fn="searchRecipesFn"
          option-label="name"
          placeholder="Zoek recipe..."
          :disabled="readonly"
          show-clear
          @item-select="onRecipeSelect(index, $event.value)"
          @clear="onRecipeClear(index)"
        >
          <template #option="{ option }">
            <div class="flex flex-col">
              <span>{{ option.name }}</span>
              <span v-if="option.type" class="text-xs text-color-secondary">{{ option.type }}</span>
            </div>
          </template>
        </SharedAutocomplete>
      </div>

      <div
        v-if="isUnsupported(entry)"
        :data-testid="`contract-recipe-unsupported-${index}`"
        class="text-sm text-orange-600 dark:text-orange-400"
      >
        Recipe-type "{{ recipeType(entry) }}" wordt nog niet ondersteund. Kies een ander recipe.
      </div>

      <div v-if="isService(entry)" class="flex flex-col gap-1">
        <label class="text-sm font-medium">Prijsmodel</label>
        <SelectButton
          :data-testid="`contract-recipe-pricing-mode-${index}`"
          :model-value="entry.pricing_mode"
          :options="PRICING_MODE_OPTIONS"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          :disabled="readonly"
          @update:model-value="updatePricingMode(index, $event)"
        />
      </div>

      <div
        v-if="showFixedPriceFields(entry) || isHourly(entry)"
        class="grid grid-cols-2 gap-3"
      >
        <div v-if="showFixedPriceFields(entry)" class="flex flex-col gap-1">
          <label class="text-sm font-medium">Tarief per beurt</label>
          <InputNumber
            :data-testid="`contract-recipe-tariff-${index}`"
            :model-value="entry.tariff"
            mode="currency"
            currency="EUR"
            locale="nl-NL"
            :min="0"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            placeholder="0,00"
            :disabled="readonly"
            :invalid="!!tariffError(entry)"
            size="small"
            @update:model-value="updateField(index, 'tariff', $event)"
          />
          <small v-if="tariffError(entry)" class="text-red-500">{{ tariffError(entry) }}</small>
        </div>

        <div v-if="isHourly(entry)" class="flex flex-col gap-1">
          <label class="text-sm font-medium">Uurtarief</label>
          <InputNumber
            :data-testid="`contract-recipe-hourly-rate-${index}`"
            :model-value="entry.hourly_running_rate"
            mode="currency"
            currency="EUR"
            locale="nl-NL"
            :min="0"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            placeholder="0,00"
            :disabled="readonly"
            :invalid="!!hourlyRateError(entry)"
            size="small"
            @update:model-value="updateField(index, 'hourly_running_rate', $event)"
          />
          <small v-if="hourlyRateError(entry)" class="text-red-500">{{ hourlyRateError(entry) }}</small>
        </div>

        <div v-if="isHourly(entry)" class="flex flex-col gap-1">
          <label class="text-sm font-medium">Draaiuren per jaar</label>
          <InputNumber
            :data-testid="`contract-recipe-hours-per-year-${index}`"
            :model-value="entry.running_hours_per_year"
            :min="0"
            :max-fraction-digits="0"
            placeholder="Aantal uren"
            :disabled="readonly"
            :invalid="!!runningHoursPerYearError(entry)"
            size="small"
            @update:model-value="updateField(index, 'running_hours_per_year', $event)"
          />
          <small v-if="runningHoursPerYearError(entry)" class="text-red-500">{{ runningHoursPerYearError(entry) }}</small>
        </div>

        <div
          v-if="isSupportedRecipeType(entry.recipe_type)"
          class="flex flex-col gap-1"
        >
          <label class="text-sm font-medium">Interval (maanden)</label>
          <InputNumber
            :data-testid="`contract-recipe-interval-${index}`"
            :model-value="entry.interval"
            :min="1"
            :max-fraction-digits="0"
            placeholder="Maanden"
            :disabled="readonly"
            :invalid="!!intervalError(entry)"
            size="small"
            @update:model-value="updateField(index, 'interval', $event)"
          />
          <small v-if="intervalError(entry)" class="text-red-500">{{ intervalError(entry) }}</small>
        </div>
      </div>

      <div
        v-if="showFixedPriceFields(entry) && entry.tariff"
        class="text-sm text-color-secondary"
      >
        Prijs per beurt: {{ formatTariff(entry.tariff) }} / elke {{ entry.interval ?? '?' }} maanden
      </div>
    </div>

    <div>
      <Button
        v-if="!readonly && !lockRecipes"
        data-testid="contract-recipe-add"
        icon="pi pi-plus"
        label="Recipe toevoegen"
        severity="secondary"
        size="small"
        outlined
        @click="addRecipe"
      />
    </div>
  </div>
</template>
